import { TimeZones } from '@constants/date';
import { TermsList } from '@types';
import { getFormattedDateTimeWithOffset } from '@utils/date';

import { formatDueDateTooltipText, getPaymentCollectionType } from '../helper';
import { getTermEndDate } from './customTerm';

// eslint-disable-next-line import/prefer-default-export
export const getDueDateTooltipText = (formData: any, isEditGroup: boolean) => {
  const {
    activationDate,
    isImmediatePayment,
    paymentCollectionType,
    intervalFrequency
  } = formData;

  return activationDate || isImmediatePayment
    ? formatDueDateTooltipText(
        isEditGroup
          ? getPaymentCollectionType(paymentCollectionType, intervalFrequency)
          : paymentCollectionType,
        intervalFrequency,
        activationDate,
        isImmediatePayment
      )
    : activationDate;
};

const normalizeDateToMidnight = (date: string | Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const isDateInPast = (date: string | Date): boolean => {
  const today = normalizeDateToMidnight(new Date());
  const compareDate = normalizeDateToMidnight(date);
  return compareDate < today;
};

const isDateCurrentOrFuture = (date: string | Date): boolean =>
  !isDateInPast(date);

const getTodayISOString = (): string =>
  getFormattedDateTimeWithOffset(new Date(), TimeZones['Asia/Kolkata']);

const validateCustomTerms = (
  termsList: TermsList[],
  originalStartDate: string
): TermsList[] => {
  if (!termsList || termsList.length === 0) return termsList;

  // If original group start date is current date or future date,
  // retain the same dates for all terms
  if (isDateCurrentOrFuture(originalStartDate)) {
    return termsList;
  }

  // If original group start date is past date,
  // recalculate all terms starting from today
  const today = new Date();
  const updatedTermsList = [...termsList];
  let currentTermStartDate = today;

  updatedTermsList.forEach((term, index) => {
    // Calculate term end date based on original term duration (months)
    const termEndDate = getTermEndDate(
      currentTermStartDate.toString(),
      term.termDuration
    );

    // Set due date same as term start date (as per existing logic)
    updatedTermsList[index] = {
      ...term,
      termStartDate: currentTermStartDate.toString(),
      termEndDate,
      dueDate: currentTermStartDate.toString()
    };

    // Next term starts the day after current term ends
    currentTermStartDate = new Date(termEndDate);
    currentTermStartDate.setDate(currentTermStartDate.getDate() + 1);
  });

  return updatedTermsList;
};

const validateEndDateWithTerms = (
  originalEndDate: string | undefined
): string => {
  // If no end date or end date is in the past, set as blank
  if (!originalEndDate || isDateInPast(originalEndDate)) {
    return '';
  }

  // If end date is current/future, retain it
  return originalEndDate;
};

export const getUpdatedCloneDetails = (dataRes: any) => {
  const {
    paymentCollectionType: sourcePaymentCollectionType,
    intervalFrequency,
    startDate: sourceStartDate,
    endDate: sourceEndDate,
    isCustomTerm,
    termsList: sourceTermsList,
    groupName,
    feeCategoryEnabled,
    feeCategoryAmountConfiguration
  } = dataRes;

  const paymentCollectionType = getPaymentCollectionType(
    sourcePaymentCollectionType,
    intervalFrequency
  );

  const startDate =
    sourceStartDate && isDateInPast(sourceStartDate)
      ? getTodayISOString()
      : sourceStartDate;

  const termsList =
    isCustomTerm && sourceTermsList
      ? validateCustomTerms(sourceTermsList, sourceStartDate)
      : sourceTermsList;

  const endDate = validateEndDateWithTerms(sourceEndDate);

  return {
    ...dataRes,
    groupName: `${groupName} Copy`,
    paymentCollectionType,
    startDate,
    endDate,
    ...(isCustomTerm && termsList && { termsList }),
    ...(feeCategoryEnabled && feeCategoryAmountConfiguration)
  };
};
